/**
 * 投资域（第 19 域 `S`）契约 —— **持仓 / 买卖赎回 / 回报率**
 *
 * 依据：`DR-059`（D1 独立投资页 · D2 移动加权 · D3 盈亏计收支 · D4 三档回报率 + XIRR）·
 * `投资域PRD_持仓与买卖` **v2**（§5 动作矩阵 / §6 口径 / §8 16 AC）·
 * 规格 §47（投资账户余额与持仓市值**分列**）/ §49（买卖资金账户必须 `investment`）·
 * `DR-036`（金额必带币种）· `DR-004`（不跨币相加）· 铁律 24（防双实现）。
 *
 * ## 🔴 五条形状纪律
 *
 * | # | 纪律 | 为什么 |
 * |:--:|---|---|
 * | 1 | **金额整数分 + 币种同现**；数量/价格 ×10⁴ | 规格 §22.1 全链路定点 |
 * | 2 | 🔴 **回报率全部由服务端算好下发** | `AC-INV13`：端侧自算 = 违规（页面数字必须等于接口值） |
 * | 3 | 🔴 **算不出来 = `null`，不是 0** | `AC-INV7`/`AC-INV12`：已清仓的浮盈率、无现金流的年化都必须是 `null` |
 * | 4 | 🔴 **`closingDiffCents` 必须如实下发** | 历史数据差 39 分是事实（PRD §1）；藏起来比差更糟 |
 * | 5 | **按币种分组返回**，不提供跨币总额 | `DR-004` 明令不跨币相加 |
 *
 * ## ⚠️ 为什么赎回没有独立的 `kind`
 *
 * 「赎回 / 取出 / 兑付」与「卖出」在**数据层完全同构**（份额减、钱回来、算盈亏）。
 * 多一个取值只会让统计要同时扫两个值（`DR-059` §12.1）—— 界面文案按品种变即可。
 */

import { z } from 'zod';
import { DayString } from './pagination.js';
import { HoldingKindValues } from '../nucleus/investment.js';
import { priceToCents } from '../nucleus/money.js';

/**
 * 成交金额与「份额 × 净值」的容差 —— 唯一实现在 `nucleus/investment.ts`（铁律 24）。
 *
 * 🔴 **按净值放大**（`grossToleranceCents`）：份额固定 `0.01` 时，一个份额最小单位
 * 对应的金额 = `净值 × 0.01` —— 高价品种（¥150/份）的尾差上限就有 75 分，
 * 固定 2 分会把**正确**的数据判成"三个数对不上"。详见 `DR-072` D4 的实施期修正。
 */
import { grossToleranceCents } from '../nucleus/investment.js';

// ─────────────────────────── 枚举 ───────────────────────────

/** 交易方向：只有两种（赎回/取出/兑付都走 `sell`） */
export const TradeKind = {
  Buy: 'buy',
  Sell: 'sell',
} as const;
export type TradeKind = (typeof TradeKind)[keyof typeof TradeKind];
export const TradeKindValues = [TradeKind.Buy, TradeKind.Sell] as const;

/**
 * 收益类型：
 * - `dividend` 分红（基金/股票）
 * - `interest` 结息（存款/理财到期或按周期付息）
 *
 * 两者**资金口径相同**（1 条 `in` 腿 + `cat-invest-gain`），差别只在 `action` 落哪个值
 * （`dividend` / `settle`），便于台账区分「持仓分来的」与「存出来的」。
 */
export const IncomeKind = {
  Dividend: 'dividend',
  Interest: 'interest',
} as const;
export type IncomeKind = (typeof IncomeKind)[keyof typeof IncomeKind];
export const IncomeKindValues = [IncomeKind.Dividend, IncomeKind.Interest] as const;

// ─────────────────────────── 请求 ───────────────────────────

const PositiveInt = (what: string) =>
  z
    .number({ message: `${what}必须是数字` })
    .int(`${what}必须是整数`)
    .positive(`${what}必须大于 0`);

/**
 * `POST /api/investments/trades` —— 买入 / 卖出。
 *
 * 🔴 **买入新建持仓**时 `name` 与 `holdingKind` 必填（`.superRefine` 先拦一道，
 * 服务层还会再判一次 —— 契约给"能早说的早说"，服务层给"绕过契约也拦得住"）。
 */
export const CreateTradeRequestSchema = z
  .object({
    kind: z.enum(TradeKindValues, { message: `交易方向只能是 ${TradeKindValues.join(' / ')}` }),
    /** 卖出必填；买入可空（空 = 新建持仓） */
    holdingId: z.string().trim().min(1).nullish(),
    /** 新建持仓时的名称 */
    name: z.string().trim().min(1, '持仓名称不得为空').max(60, '持仓名称不超过 60 字').nullish(),
    code: z.string().trim().max(30, '代码不超过 30 字').nullish(),
    holdingKind: z.enum(HoldingKindValues, { message: '持仓品种不在允许范围内' }).nullish(),
    /** 数量 ×10⁴（金额制品种由端侧按现价折算出份额后传入，见 PRD §5） */
    quantityE4: PositiveInt('数量'),
    /** 价格：万分之一元/份（金额制品种填当前净值） */
    priceE4: PositiveInt('价格'),
    /**
     * **成交金额**（原币整数分）—— 🆕 `DR-072` `D2` · **资金腿与盈亏的唯一权威**
     *
     * 三个数（净值 / 份额 / 金额）精度不同，乘积必然有舍入 ⇒ 允许它与
     * `priceToCents(quantityE4, priceE4)` **不相等**（差值上限见 {@link GROSS_TOLERANCE_CENTS}）。
     *
     * - **给了** ⇒ 资金腿金额 = 它；`realized_pnl = gross − 成本`；汇总与年化也取它
     * - **不给** ⇒ 服务端按 `priceToCents(quantityE4, priceE4)` 补齐（向后兼容：老客户端 / 小程序）
     *
     * 🔴 语义是"**实付 / 实收**"，**不含手续费**（手续费独立成笔，`AC-INV6`）。
     */
    grossAmountCents: z
      .number({ message: '成交金额必须是数字' })
      .int('成交金额必须是整数分')
      .positive('成交金额必须大于 0')
      .optional(),
    /**
     * 份额是否由「成交金额 ÷ 净值」**反算**而来 —— 🆕 `DR-072` `D5`。
     *
     * 只影响一件事：**反算**出的份额超过持仓时按「全部赎回」夹持（并改写成交金额），
     * 而不是 `400`；用户**手输**的份额超过持仓仍然 `400`（不做卖空，`AC-INV3`）。
     */
    qtyDerivedFromAmount: z.boolean().optional(),
    /**
     * 手续费（整数分，可空）。
     * 🔴 **不进持仓成本**（`AC-INV6`）：> 0 时服务端**额外**生成一笔独立支出交易，
     * 挂 `cat-other-fee`（`DR-059` §12.5，与转账手续费同规则）。
     */
    feeCents: z.number().int().nonnegative('手续费不得为负').optional(),
    /** 资金账户 —— 必须 `type='investment'` 且与持仓同币种（`AC-INV1`） */
    accountId: z.string().trim().min(1, '资金账户不得为空'),
    date: DayString,
    note: z.string().trim().max(200, '备注不超过 200 字').optional(),
    /** 到期日（仅理财/存款/债券有意义；新建持仓时可一并登记） */
    maturityDate: DayString.nullish(),
  })
  .superRefine((v, ctx) => {
    if (v.kind === TradeKind.Sell && (v.holdingId === null || v.holdingId === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['holdingId'],
        message: '卖出必须指定持仓（没有持仓就无从卖）',
      });
    }
    // 买入且没给 holdingId ⇒ 是"新建持仓"⇒ 名称与品种必填
    if (v.kind === TradeKind.Buy && (v.holdingId === null || v.holdingId === undefined)) {
      if (v.name === null || v.name === undefined || v.name.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['name'],
          message: '新建持仓必须填名称',
        });
      }
      if (v.holdingKind === null || v.holdingKind === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['holdingKind'],
          message: '新建持仓必须选品种',
        });
      }
    }
    // 三个数必须"对得上"：差额超过容差 ⇒ 400（`DR-072` D4 / `AC-INV19`）
    if (v.grossAmountCents !== undefined) {
      const expected = priceToCents(v.quantityE4 as never, v.priceE4 as never) as number;
      const diff = v.grossAmountCents - expected;
      // 🔴 容差要同时看净值与份额（批 126 修正：漏了份额项会拦掉 5~6 位净值的真实对账单）
      const tolerance = grossToleranceCents(v.priceE4, v.quantityE4);
      if (diff > tolerance || diff < -tolerance) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['grossAmountCents'],
          message:
            `三个数对不上：份额 × 净值 = ${String(expected)} 分，成交金额 = ${String(v.grossAmountCents)} 分` +
            `（相差 ${String(diff)} 分，上限 ${String(tolerance)} 分）—— 请核对三个数`,
        });
      }
    }
  });
export type CreateTradeRequest = z.infer<typeof CreateTradeRequestSchema>;

/** `POST /api/investments/incomes` —— 分红 / 结息（只记收益，不动数量与成本） */
export const RecordInvestmentIncomeSchema = z.object({
  holdingId: z.string().trim().min(1, '必须指定持仓'),
  kind: z.enum(IncomeKindValues, { message: `收益类型只能是 ${IncomeKindValues.join(' / ')}` }),
  /** 收益金额（整数分） */
  amountCents: PositiveInt('收益金额'),
  /** 到账账户；省略 = 持仓的默认账户 */
  accountId: z.string().trim().min(1).nullish(),
  date: DayString,
  note: z.string().trim().max(200, '备注不超过 200 字').optional(),
});
export type RecordInvestmentIncome = z.infer<typeof RecordInvestmentIncomeSchema>;

/**
 * `PATCH /api/investments/holdings/:id/price` —— **更新现价**（唯一能改市值的入口）。
 *
 * 🔴 只改 `current_price`：数量 / 成本价 / 已实现盈亏**都不许**同批改
 * （`AC-INV14` 明写"改价后这三项不变"）。
 */
export const UpdateHoldingPriceRequestSchema = z.object({
  currentPriceE4: z.number().int('现价必须是整数（万分之一元）').positive('现价必须大于 0'),
});
export type UpdateHoldingPriceRequest = z.infer<typeof UpdateHoldingPriceRequestSchema>;

/** `PATCH /api/investments/holdings/price` —— 批量改价（页头「批量改价」） */
export const BatchUpdatePriceRequestSchema = z.object({
  items: z
    .array(
      z.object({
        holdingId: z.string().trim().min(1),
        currentPriceE4: z.number().int().positive('现价必须大于 0'),
      }),
    )
    .min(1, '至少给一条')
    .max(200, '一次最多 200 条'),
});
export type BatchUpdatePriceRequest = z.infer<typeof BatchUpdatePriceRequestSchema>;

/**
 * `GET /api/investments/reconcile` 的 query —— 两个都可空。
 *
 * 都不给 ⇒ **全量对账**（报表侧从"很早"到今天）。这与报表的默认窗口（最近 N 个月）
 * **刻意不同**：对账要回答的是"历史数据有没有进报表"，窗口太短会把历史差额藏起来。
 */
export const ReconcileQuerySchema = z.object({
  from: DayString.optional(),
  to: DayString.optional(),
});
export type ReconcileQuery = z.infer<typeof ReconcileQuerySchema>;

// ─────────────────────────── 视图 ───────────────────────────

/** 一条持仓（**全部派生数由服务端算好**，端侧只负责显示） */
export interface HoldingView {
  id: string;
  accountId: string | null;
  accountName: string | null;
  kind: string;
  code: string | null;
  name: string | null;
  currency: string;
  /** 数量 ×10⁴ */
  quantityE4: number;
  /** 成本价 / 现价（万分之一元每份） */
  costPriceE4: number;
  currentPriceE4: number;
  /** 剩余成本 / 市值 / 浮盈（整数分） */
  costCents: number;
  marketValueCents: number;
  unrealizedCents: number;
  /** 浮盈率（万分之一）；🔴 成本为 0 ⇒ `null` */
  unrealizedReturnE4: number | null;
  /** 该持仓历史已实现盈亏 / 累计分红（整数分） */
  realizedPnlCents: number;
  dividendCents: number;
  /** 占**同币种**持仓市值的比例（万分之一）；同币种市值为 0 ⇒ `null` */
  weightE4: number | null;
  /** `YYYY-MM-DD`；`null` = 未填（**不是"不到期"**） */
  maturityDate: string | null;
}

/** 年化（`AC-INV12`：数字与"持有天数 / 样本是否过短"必须同屏下发） */
export interface XirrView {
  /** 年化率（万分之一，`16230` = 162.30%） */
  pctE4: number;
  /** 从首笔现金流到今天的天数 */
  holdingDays: number;
  /** 🔴 `holdingDays < 90` ⇒ 样本过短，端侧必须加警示（`DR-059` D4） */
  thinSample: boolean;
}

/**
 * 一个币种的汇总（`DR-004`：**不跨币相加**）。
 *
 * 六个数字的关系见 `nucleus/investment.ts` 的 `composeInvestmentReturn()`；
 * 🔴 端侧**不得**自行 `reduce` 持仓来得到这些数（`AC-INV13`）。
 */
export interface CurrencySummaryView {
  currency: string;
  investedCents: number;
  proceedsCents: number;
  dividendCents: number;
  realizedPnlCents: number;
  holdingCostCents: number;
  marketValueCents: number;
  unrealizedCents: number;
  totalPnlCents: number;
  totalReturnE4: number | null;
  realizedReturnE4: number | null;
  /** 闭合差（新交易恒 0；历史数据如实给差值） */
  closingDiffCents: number;
  /** 年化；无从计算 ⇒ `null`（**不是 0%**） */
  xirr: XirrView | null;
}

/** `GET /api/investments/overview` */
export interface InvestmentOverviewView {
  holdings: HoldingView[];
  /** 按币种分块；顺序 = 持仓里出现顺序（本币优先由服务端排好） */
  byCurrency: CurrencySummaryView[];
}

/** `POST /api/investments/trades` 的响应 */
export interface TradeResultView {
  /** 买卖主交易 id（资金腿在里面） */
  transactionId: string;
  /** 手续费交易 id（`feeCents > 0` 时才有） */
  feeTransactionId: string | null;
  holdingId: string;
  /** 卖出才有：本次已实现盈亏 */
  realizedPnlCents: number | null;
  /** 动作后的持仓（端侧直接替换，不必再拉一次） */
  holding: HoldingView;
}

/**
 * 一笔投资流水（撤销入口的列表行）—— 批 `127` · `DR-073`。
 *
 * 🔴 `grossCents` 为 `null` 的是 v1 迁移的历史行（那时还没有成交金额这一列）——
 * 界面**不要拿 0 冒充**，直接不显示金额。
 */
export interface InvestmentTradeRowView {
  id: string;
  /** `buy` / `sell` / `dividend` / `settle` … */
  action: string;
  /** `YYYY-MM-DD` */
  date: string;
  quantityE4: number | null;
  priceE4: number | null;
  /** 成交金额（整数分）；历史行 ⇒ `null` */
  grossCents: number | null;
  /** 已实现盈亏（卖出）/ 金额（分红结息），整数分 */
  realizedPnlCents: number | null;
  /**
   * 是否可撤销 —— **只有最新一笔**、且是买卖、且有资金流水时为真。
   *
   * 🔴 撤销是栈式的（`DR-073` D2）：撤中间那笔会让后面卖出的成本对不上。
   */
  revocable: boolean;
}

/** `GET /api/investments/holdings/:id/trades` */
export interface InvestmentTradesView {
  holdingId: string;
  /** 按日期倒序（最新在前）—— `revocable` 只可能落在第一项 */
  items: InvestmentTradeRowView[];
}

/** `DELETE /api/investments/trades/:id` 的响应 */
export interface RevokeTradeResultView {
  holdingId: string;
  /** 撤销后的持仓（端侧直接替换，不必再拉一次概览） */
  holding: HoldingView;
}

/** `POST /api/investments/incomes` 的响应 */
export interface IncomeResultView {
  transactionId: string;
  holdingId: string;
  /** 本笔记入的收益金额 */
  amountCents: number;
  holding: HoldingView;
}

/** `PATCH .../price` 的响应 */
export interface UpdatePriceResult {
  updated: number;
  holdings: HoldingView[];
}

// ─────────────────────── 对账（批 99 B3 · `AC-INV9` 报表侧） ───────────────────────

/** 一个币种的对账结果 */
export interface ReconcileCurrencyView {
  currency: string;
  /** **报表口径**：`legs` 里挂 `cat-invest-gain` 的计入收支腿合计（本币） */
  reportGainCents: number;
  /** **报表口径**：`legs` 里挂 `cat-invest` 的净支出（本币，恒 ≥ 0） */
  reportLossCents: number;
  /** **投资域口径**：盈利卖出的已实现盈亏 + 分红 / 结息（原币） */
  domainGainCents: number;
  /** **投资域口径**：亏损卖出的已实现亏损绝对值（原币） */
  domainLossCents: number;
  /** 差额 = 报表 − 投资域（正 = 报表多记） */
  gainDiffCents: number;
  lossDiffCents: number;
  /**
   * 逐分为 0 才算对上。
   *
   * 🔴 历史数据（v1 迁移的已实现盈亏 / 分红）**没有资金腿** ⇒ 必然为 `false`
   * （`DR-059` D7：不补造流水，只如实标注）。
   */
  reconciled: boolean;
  /**
   * 该币种是否**与报表口径可比**。
   *
   * `false` 的两种情形（都必须给 `reason`）：
   * - 非本币：报表只出本币合计，无法单独分离该币种；
   * - 账本里有外币投资流水：报表侧 `cat-invest-gain` 已把外币折算进来，逐币比对会假性不等。
   */
  comparable: boolean;
  /** `comparable === false` 时的原因（端侧原样展示，不改写） */
  reason: string | null;
}

/** `GET /api/investments/reconcile` */
export interface InvestmentReconcileView {
  /** 对账窗口（账本本地日，含两端） */
  window: { from: string; to: string };
  /** 报表口径的币种（= 账本本币） */
  baseCurrency: string;
  items: ReconcileCurrencyView[];
  /** 🔴 报表侧缺汇率 ⇒ 本币合计不完整（`AC-G1.31` 同一条纪律） */
  fxIncomplete: boolean;
}
